// The one authorized Library feed: GET /api/v1/company/library (ART's src/company-workspace/library-feed.d.mts).
// Pending-contract: these types mirror that ABI until the PR2 root carries it.
import { CompanyRequestError, companyRequest } from '@/company/api';

export type LibraryGroup = 'all' | 'documents' | 'decks' | 'sheets' | 'files';
export type LibrarySegment = 'all' | 'mine' | 'shared';
export type LibraryFeedOpenTarget =
  | { kind: 'result' | 'file'; id: string }
  | {
      kind: 'artifact';
      id: string;
      versionId: string;
      revisionId: string;
      versionNumber: number;
      purpose: 'editor' | 'viewer';
    };
export interface LibraryFeedItem {
  id: string;
  kind:
    | 'document'
    | 'report'
    | 'deck'
    | 'spreadsheet'
    | 'table'
    | 'chart'
    | 'result'
    | 'page'
    | 'file';
  group: Exclude<LibraryGroup, 'all'>;
  title: string;
  owner: { id: string | null; label: string | null };
  mine: boolean;
  shared: boolean;
  audience: string | null;
  state: 'draft' | 'ready' | 'changed-since-ready' | null;
  updatedAt: string;
  thumbnail: { ref: string | null; picture: string | null };
  canEdit: boolean;
  /** Missing on older servers; only explicit permission shows deletion controls. */
  canDelete?: boolean;
  openTarget: LibraryFeedOpenTarget;
  mime?: string | null;
  processingStatus?: string | null;
}
export interface LibraryFeedPage {
  schemaVersion: 1;
  items: LibraryFeedItem[];
  total: number;
  catalogueGeneration: string;
  nextCursor: string | null;
  appsMatchCount: number;
  search: { semanticStatus: string; coverage: unknown; degraded: boolean } | null;
}
export type LibraryFeedRead = {
  segment: LibrarySegment;
  group: LibraryGroup;
  query: string;
  cursor: string | null;
  limit?: number;
};

/** The server this page talks to does not serve the feed (an older install): the caller keeps the legacy Library. */
export class LibraryFeedUnavailable extends Error {
  constructor() {
    super('library_feed_unavailable');
  }
}

export async function readLibraryFeed(
  input: LibraryFeedRead,
  /** Absent: the page's own fetch, as the other Library list reads use. */
  fetchImpl?: typeof fetch,
): Promise<LibraryFeedPage> {
  const query = new URLSearchParams({
    segment: input.segment,
    group: input.group,
    limit: String(input.limit ?? 20),
  });
  if (input.query.trim()) query.set('query', input.query.trim());
  if (input.cursor) query.set('cursor', input.cursor);
  try {
    const page = await companyRequest<LibraryFeedPage>(
      `/api/v1/company/library?${query}`,
      'GET',
      undefined,
      fetchImpl,
    );
    // Anything that is not the feed's own page (an older server's answer) keeps the legacy Library.
    if (!page || page.schemaVersion !== 1 || !Array.isArray(page.items))
      throw new LibraryFeedUnavailable();
    return page;
  } catch (error) {
    if (error instanceof CompanyRequestError && (error.status === 404 || error.status === 501))
      throw new LibraryFeedUnavailable();
    throw error;
  }
}
